import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  input,
  Input,
  Output,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MultiSelectFieldComponent } from '../multiselect-field/multiselect-field.component';
import {
  Item,
  SelectFieldComponent,
} from '../select-field/select-field.component';
import { TipologicheService } from './select-tipologica-field.service';
import { ListaTipologicheStore } from './select-tipologica-field.store';

@Component({
  selector: 'siat-select-tipologica-field',
  templateUrl: './select-tipologica-field.component.html',
  styleUrls: ['./select-tipologica-field.component.scss'],
  imports: [FormsModule, SelectFieldComponent, MultiSelectFieldComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectTipologicaFieldComponent),
      multi: true,
    },
    ListaTipologicheStore,
    TipologicheService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectTipologicaFieldComponent
  implements AfterViewInit, ControlValueAccessor
{
  /**
   * The form group associated with the input field.
   */
  @Input()
  formGroup: FormGroup;

  /**
   * The control name associated with the input field.
   */
  @Input()
  controlName: string;

  /**
   * The id attribute of the input field.
   */
  @Input()
  id?: string;

  /**
   * Th placeholder of the input field.
   */
  @Input()
  placeholder?: string;

  /**
   * The input label.
   */
  @Input()
  label: string;

  /**
   * The input "name" attribute value.
   */
  @Input()
  name?: string;

  /**
   * The endpoint.
   */
  endpoint = input<string>('');

  /**
   * Indicates if the field can be searched by findAll.
   */
  @Input()
  withFindAll = true;

  /**
   * The output event emitter.
   */
  @Output()
  optionReady = new EventEmitter<boolean>();

  /**
   * Indicates if the field is multiselect.
   */
  @Input({ transform: booleanAttribute })
  areMultiselect = false;

  /**
   * filteredOptions
   */
  @Input()
  filterRules: { name: string; action: string }[] = [];

  /**
   * The empty message if no items are available.
   */
  emptyMessage = input<string>('');

  /**
   * Gets the form control associated with the input field.
   */
  get control(): FormControl<Item | Item[]> {
    return this.formGroup.controls[this.controlName] as FormControl<Item[]>;
  }

  /**
   * Gets a flag telling if the the form control is in a disabled state.
   */
  get disabled(): boolean {
    return this.control.disabled;
  }

  /**
   * Search text.
   */
  private searchText = '';

  /**
   * Gets the search text.
   */
  get search(): string {
    return this.searchText;
  }

  /**
   * Indicates if the view is ready.
   */
  viewReady = signal(false);

  /**
   * The store.
   */
  protected store = inject(ListaTipologicheStore);

  /**
   * A signal containing the items.
   */
  private allItems = this.store.listaTipologicaItemResponse;

  /**
   * A signal containing the filtered items.
   */
  public items = computed(() => {
    const originalItems = this.allItems();
    const rules = this.filterRules;

    if (!rules || rules.length === 0 || !originalItems) return originalItems;

    const inclusionRules = rules.filter(rule => rule?.action === 'include');

    if (inclusionRules.length > 0) {
      const inclusionNames = inclusionRules.map(rule => rule.name);
      return originalItems.filter(item => inclusionNames.includes(item.name));
    }

    const exclusionRules = rules.filter(rule => rule?.action === 'exclude');

    if (exclusionRules.length > 0) {
      const exclusionNames = exclusionRules.map(rule => rule.name);
      return originalItems.filter(item => !exclusionNames.includes(item.name));
    }

    return originalItems;
  });

  /**
   * Constructor for the SelectTipologicaFieldComponent.
   * It sets up an effect that listens for changes in the items and control value.
   * If items are available, it emits an event indicating readiness and updates the control value if necessary.
   * If no items are available, it emits an event indicating unavailability.
   */
  constructor() {
    effect(() => {
      const endpoint = this.endpoint();
      const areDisabled = this.control.disabled;
      if (!areDisabled && endpoint) {
        this.loadGetAllTipologicaItem();
      }
    });

    effect(() => {
      const currentItems = this.allItems();
      const currentValue = this.control.value as unknown;

      if (currentItems && currentItems.length > 0) {
        this.optionReady.emit(true);

        if (currentValue) {
           const findValue = currentItems.find(
            item => item.name === currentValue
          );
          if (findValue && this.control.value !== findValue) {
            this.control.patchValue(findValue, { emitEvent: false });
          }
        }
      } else {
        this.optionReady.emit(false);
      }
    });
  }

  loadGetAllTipologicaItem() {
    if (this.endpoint) {
      this.store.fetchGetAllTipologicaItem([this.endpoint(), this.withFindAll]);
    }
  }

  /**
   * @inheritdoc
   */
  ngAfterViewInit(): void {
    this.viewReady.set(true);
  }

  /**
   * Keeps a reference to the function to be called every time
   * the value of the field is changed in the UI.
   */
  onChange: (value: Item | Item[]) => void = () => {};

  /**
   * Keeps a reference to the function to be called every time
   * the field has to be considered touched (normally on blur).
   */
  onTouched: () => void = () => {};

  /**
   * Registers a function to call when the field value changes in the UI.
   * This is needed to communicate the update to angular forms API.
   */
  registerOnChange(changeCallback: (value: Item | Item[]) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * Allows to register a function to be called when the field is touched
   * (it's a "noop" method but it's needed to correctly implement ControlValueAccessor interface).
   */
  registerOnTouched(touchedCallback: () => void) {
    this.onTouched = touchedCallback;
  }

  /**
   * @inheritDoc
   */
  writeValue(value: Item | Item[]): void {
    this.control.setValue(value);
  }

  checkIfEmtpy(close: boolean): void {
    if (close) return;
    this.control.markAsTouched();
  }
}
